import { BaseCategory } from "./BaseCategory";
import { AuthoringBlockType, AuthoringElementType, CalloutType, ConnectorType, HeaderPositionType, NodeType, PaletteColorType, TextStyleType } from "../constants";
import { SlideCategories } from "./CategoryController";
import { SlideModelBuilder } from "./SlideModelBuilder";
import { v4 as uuid } from "uuid";

export class FlowChartCategory extends BaseCategory {
    get name() {
        return "FlowChart";
    }

    get type() {
        return SlideCategories.FLOWCHART;
    }

    get icon() {
        return null;
    }

    get description() {
        return `The content describes a flowchart with decision points, branching paths, or conditional logic. Use for process flows with decisions, branching logic, or conditional paths. For simple linear processes, use Flow Diagram Category.`;
    }

    get slideTypes() {
        return [];
    }

    getPrompt() {
        return `Parse the content to determine the steps, decision points, and flow paths in the flowchart. Identify connections between steps and any branching or conditional logic. List nodes and their relationships in order. Separate any additional content into the conclusion`;
    }

    getImagePrompt() {
        return false;
    }

    getModelHints() {
        return {
            flowchartType: "decision tree | branching process | conditional flow | algorithm flow",
        };
    }

    getPromptSchema() {
        return {
            flowchartTitle: "optional title or main heading for the flowchart",
            nodes: [{
                id: "unique identifier for this node",
                label: "string - the text/label for this node",
                description: "optional string - additional details about this step",
                type: "process | decision | start | end | data",
                x: "optional number - horizontal position (0-100 as percentage)",
                y: "optional number - vertical position (0-100 as percentage)",
            }],
            connections: [{
                from: "id of the source node",
                to: "id of the target node",
                label: "optional string - label for the connection (e.g., 'Yes', 'No')"
            }],
            conclusion: "an optional short conclusion or takeaway for the slide",
        };
    }

    validateGeneratedModel(model) {
        if (!this.validateArray(model.nodes)) {
            return false;
        }
        return true;
    }

    getSlideModels(model, options = {}) {
        let models = [
            this.buildVerticalFlowChart(model, options),
            this.buildHorizontalFlowChart(model, options),
        ];

        return models;
    }

    buildVerticalFlowChart(model, options = {}) {
        let builder = new SlideModelBuilder("flow_chart", model);
        builder.createHeader({ heading: model.slideTitle, position: HeaderPositionType.TOP });

        const nodeItems = [];
        const connectorItems = [];

        const canvasWidth = 1180;
        const canvasHeight = 475;

        const nodeYMin = model.nodes.reduce((min, n) => n.y !== undefined ? Math.min(min, n.y) : min, 100);
        const nodeYMax = model.nodes.reduce((max, n) => n.y !== undefined ? Math.max(max, n.y) : max, 0);
        const nodeYRange = nodeYMax - nodeYMin || 1;

        // spread nodes vertically using min and max y to recalc 0-100 within canvasHeight
        model.nodes.forEach(n => {
            if (n.y !== undefined) {
                n.y = ((n.y - nodeYMin) / nodeYRange) * 100;
            }
        });

        // Create nodes
        if (model.nodes && model.nodes.length > 0) {
            const verticalSpacing = 70 / (model.nodes.length - 1 || 1);

            model.nodes.forEach((node, index) => {
                const nodeId = node.id || uuid();

                // Determine callout type based on node type
                let nodeOptions = {
                    calloutType: CalloutType.BOX,
                    width: 180,
                    height: 80,
                    fitToText: true,
                    color: null
                };

                if (node.type === "decision") {
                    nodeOptions.calloutType = CalloutType.DIAMOND;
                    nodeOptions.width = 180;
                    nodeOptions.height = 100;
                    nodeOptions.fitToText = false;
                } else if (node.type === "start" || node.type === "end") {
                    nodeOptions.calloutType = CalloutType.CAPSULE;
                    nodeOptions.width = 180;
                    nodeOptions.height = 60;
                    nodeOptions.color = PaletteColorType.ACCENT1;
                } else if (node.type === "data") {
                    nodeOptions.calloutType = CalloutType.CAPSULE;
                    nodeOptions.width = 120;
                    nodeOptions.height = 120;
                }

                const blocks = [];
                if (node.label) {
                    blocks.push(builder.createBlock(node.label, TextStyleType.TITLE));
                }
                // if (node.description) {
                //     blocks.push(builder.createBlock(node.description, TextStyleType.BODY));
                // }

                nodeItems.push({
                    id: nodeId,
                    type: AuthoringElementType.CALLOUT,
                    ...nodeOptions,
                    decorationStyle: "filled",
                    nodeType: blocks.length > 1 ? NodeType.TEXT : NodeType.TEXT,
                    x: canvasWidth * (node.x ?? 50) / 100,  // Center horizontally by default
                    y: canvasHeight * (node.y ?? (15 + index * verticalSpacing)) / 100,  // Space vertically
                    text: blocks.length > 0 ? { blocks, blockFontScales: { title: 0.66 } } : undefined,
                });
            });

            // Create connectors
            if (model.connections && model.connections.length > 0) {
                model.connections.forEach(connection => {
                    const sourceNode = model.nodes.find(n => (n.id || n.label) === connection.from);
                    const targetNode = model.nodes.find(n => (n.id || n.label) === connection.to);

                    if (sourceNode && targetNode) {
                        const sourceId = sourceNode.id || uuid();
                        const targetId = targetNode.id || uuid();

                        const connector = {
                            id: uuid(),
                            source: sourceId,
                            target: targetId,
                            connectorType: ConnectorType.STEP,
                            endDecoration: "arrow",
                        };

                        if (connection.label) {
                            connector.labels = [{
                                text: {
                                    blocks: [{
                                        html: connection.label,
                                        textStyle: TextStyleType.BODY,
                                        type: AuthoringBlockType.TEXT,
                                    }]
                                }
                            }];
                        }

                        connectorItems.push(connector);
                    }
                });
            }
        }

        let primary = {
            elements: nodeItems,
            connections: {
                items: connectorItems
            },
            showGrid: false,
            snapToGrid: true,
        };

        builder.createPrimary(primary);

        // if (model.conclusion) {
        //     builder.createBottomTray(model.conclusion);
        // }

        if (options.citeSources) {
            builder.createSourcesAttribution(model.sources);
        }

        return builder.model;
    }

    buildHorizontalFlowChart(model, options = {}) {
        let builder = new SlideModelBuilder("flow_chart", model);
        builder.createHeader({ heading: model.slideTitle, position: HeaderPositionType.TOP });

        const nodeItems = [];
        const connectorItems = [];

        const canvasWidth = 1180;
        const canvasHeight = 430;

        // Create nodes in horizontal layout
        if (model.nodes && model.nodes.length > 0) {
            const horizontalSpacing = 70 / (model.nodes.length - 1 || 1);

            model.nodes.forEach((node, index) => {
                const nodeId = node.id || uuid();

                // Determine callout type based on node type
                let calloutType = CalloutType.BOX;
                let width = 140;
                let height = 100;

                if (node.type === "decision") {
                    calloutType = CalloutType.DIAMOND;
                    width = 140;
                    height = 140;
                } else if (node.type === "start" || node.type === "end") {
                    calloutType = CalloutType.CAPSULE;
                    width = 140;
                    height = 80;
                } else if (node.type === "data") {
                    calloutType = CalloutType.CIRCLE;
                    width = 110;
                    height = 110;
                }

                const blocks = [];
                if (node.label) {
                    blocks.push(builder.createBlock(node.label, TextStyleType.TITLE));
                }
                if (node.description) {
                    blocks.push(builder.createBlock(node.description, TextStyleType.BODY));
                }

                nodeItems.push({
                    id: nodeId,
                    type: AuthoringElementType.CALLOUT,
                    calloutType,
                    nodeType: blocks.length > 1 ? NodeType.TEXT : NodeType.TEXT,
                    x: canvasWidth * (node.x ?? (15 + index * horizontalSpacing) / 100),  // Space horizontally
                    y: canvasHeight * (node.y ?? 50),  // Center vertically by default
                    width,
                    height,
                    color: node.type === "start" || node.type === "end" ? PaletteColorType.THEME : PaletteColorType.SECONDARY,
                    fitToText: true,
                    text: blocks.length > 0 ? { blocks } : undefined,
                });
            });

            // Create connectors (same as vertical)
            if (model.connections && model.connections.length > 0) {
                model.connections.forEach(connection => {
                    const sourceNode = model.nodes.find(n => (n.id || n.label) === connection.from);
                    const targetNode = model.nodes.find(n => (n.id || n.label) === connection.to);

                    if (sourceNode && targetNode) {
                        const sourceId = sourceNode.id || uuid();
                        const targetId = targetNode.id || uuid();

                        const connector = {
                            id: uuid(),
                            source: sourceId,
                            target: targetId,
                            connectorType: ConnectorType.STEP,
                            endDecoration: "arrow",
                        };

                        if (connection.label) {
                            connector.labels = [{
                                blocks: [{
                                    html: connection.label,
                                    textStyle: TextStyleType.BODY,
                                    type: AuthoringBlockType.TEXT,
                                }]
                            }];
                        }

                        connectorItems.push(connector);
                    }
                });
            }
        }

        let primary = {
            elements: nodeItems,
            connections: {
                items: connectorItems
            },
            showGrid: false,
            snapToGrid: true,
        };

        builder.createPrimary(primary);

        if (options.citeSources) {
            builder.createSourcesAttribution(model.sources);
        }

        if (model.conclusion) {
            builder.createBottomTray(model.conclusion);
        }

        return builder.model;
    }
}
